// api/aws/useTimeline.ts
import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios, { AxiosResponse } from 'axios'
import type { Timeline } from 'types'

/** Fetch a single deal's timeline (by id). */
export async function getTimeline(id: string): Promise<Timeline[]> {
  try {
    const url = id ? `${baseUrl}/aws/timeline/${id}` : `${baseUrl}/aws/timeline`
    const resp: AxiosResponse<Timeline[] | { data?: Timeline[] }> = await axios.get(url, {
      params: id ? undefined : { table: 'timeline' },
    })

    if (resp.status === 204) return []

    const payload = resp.data
    // API may return either an array or an object with `data`
    if (Array.isArray(payload)) return payload
    if (payload && Array.isArray(payload.data)) return payload.data

    return []
  } catch (err) {
    console.error('Error fetching timeline:', err)
    return []
  }
}

export type UseTimelineResult = {
  timeline: Timeline[]
  isLoading: boolean
  isError: boolean
}

/**
 * React Query hook to load a deal's timeline by id.
 * Only runs when `timelineId` is truthy.
 */
export function useTimeline(timelineId?: string): UseTimelineResult {
  const { data, isLoading, isError }: UseQueryResult<Timeline[]> = useQuery({
    queryKey: ['timeline', timelineId ?? 'none'],
    queryFn: () => getTimeline(timelineId as string),
    enabled: Boolean(timelineId),
    staleTime: Infinity,
    gcTime: 10 * 60 * 1000,
  })

  return {
    timeline: data ?? [],
    isLoading,
    isError,
  }
}
